import React, { useState } from 'react';
import { Card, Button, Flex, Typography, theme, Modal, Dropdown, notification, Tag } from 'antd';
import { EditOutlined, DeleteOutlined, CopyOutlined, MoreOutlined, FileTextOutlined } from '@ant-design/icons';
import { CustomIntegration } from '../CustomIntegration/CustomIntegration';

import { createLogger } from '../../../../../../utils/logger';
const logger = createLogger('features-assistants');

const { useToken } = theme;
const { Text, Paragraph } = Typography;

interface CardCustomActionProps {
  message: string;
  onDelete: () => void;
  onDuplicate: () => void;
  onEdit: (newMessage: string) => void;
  chatbotId?: string;
  flowId?: string;
}

export const CardCustomAction: React.FC<CardCustomActionProps> = ({
  message,
  onDelete,
  onDuplicate,
  onEdit,
  chatbotId,
  flowId
}) => {
  const { token } = useToken();
  const [showEditModal, setShowEditModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Extraer los datos del mensaje personalizado
  const extractCustomData = (msg: string) => {
    try {
      const match = msg.match(/\{.*\}/);
      if (match) {
        const customData = JSON.parse(match[0]);
        return {
          prompt: customData.prompt || '',
          documentName: customData.documentName || null,
          documentContent: customData.documentContent || null,
          timestamp: customData.timestamp || new Date().toISOString()
        };
      }
      return {
        prompt: '',
        documentName: null,
        documentContent: null,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.error('Error parsing custom message:', error);
      return {
        prompt: '',
        documentName: null,
        documentContent: null,
        timestamp: new Date().toISOString()
      };
    }
  };
  
  const customData = extractCustomData(message);
  
  const handleEditClick = () => {
    setShowEditModal(true);
  };
  
  const handleFormSave = (newMessage: string) => {
    onEdit(newMessage);
    setShowEditModal(false);
  };
  
  const handleFormCancel = () => {
    setShowEditModal(false);
  };

  const handleDeleteClick = async () => {
    setIsDeleting(true);
    try {
      notification.success({
        message: 'Success',
        description: 'Custom integration deleted successfully'
      });
      onDelete();
    } catch (error: any) {
      logger.error('Error in delete process:', error);
      notification.error({
        message: 'Error',
        description: error?.message || 'Failed to delete custom integration'
      });
    } finally {
      setIsDeleting(false);
    }
  };
  
  const menuItems = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <EditOutlined />,
      onClick: handleEditClick
    },
    {
      key: 'duplicate',
      label: 'Duplicate',
      icon: <CopyOutlined />,
      onClick: onDuplicate
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      onClick: handleDeleteClick,
      disabled: isDeleting
    }
  ];

  // Función para truncar texto largo
  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };
  
  return (
    <>
      <Card 
        style={{ 
          maxWidth: '400px',
          width: '100%',
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowTertiary,
          cursor: 'pointer',
          borderLeft: `4px solid ${token.colorPrimary}`
        }}
        bodyStyle={{ padding: token.paddingSM }}
        onClick={handleEditClick}
      >
        <Flex align="center" justify="space-between" style={{ width: '100%' }}>
          <Flex gap={token.marginMD} align="center" style={{ flex: 1 }}>
            <div 
              style={{ 
                width: 50,
                height: 50,
                borderRadius: token.borderRadiusLG,
                backgroundColor: token.colorPrimaryBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <FileTextOutlined 
                style={{ 
                  fontSize: '24px', 
                  color: token.colorPrimary 
                }} 
              />
            </div>
            <Flex vertical gap={token.marginXS} style={{ flex: 1, minWidth: 0 }}>
              <Flex align="center" gap={token.marginXS}>
                <Text strong style={{ fontSize: '16px', lineHeight: '24px', margin: 0 }}>
                  Custom Integration
                </Text>
                <Tag color="blue">Custom</Tag>
              </Flex>
              <Paragraph 
                type="secondary" 
                style={{ 
                  fontSize: '14px', 
                  lineHeight: '20px', 
                  margin: 0,
                  wordBreak: 'break-word'
                }}
                ellipsis={{ rows: 2, tooltip: customData.prompt }}
              >
                {truncateText(customData.prompt, 80)}
              </Paragraph>
              {customData.documentName && (
                <Flex align="center" gap={token.marginXS}>
                  <FileTextOutlined style={{ color: token.colorTextSecondary, fontSize: '12px' }} />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {customData.documentName}
                  </Text>
                </Flex>
              )}
            </Flex>
          </Flex>
          <div onClick={(e) => e.stopPropagation()}>
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button 
                icon={<MoreOutlined />} 
                size="small"
                type="text"
                loading={isDeleting}
              />
            </Dropdown>
          </div>
        </Flex>
      </Card>
      
      <Modal
        title="Edit Custom Integration"
        open={showEditModal}
        onCancel={handleFormCancel}
        footer={null}
        width={700}
        destroyOnClose={true}
      >
        <CustomIntegration
          onBack={handleFormCancel}
          onSave={handleFormSave}
          chatbotId={chatbotId}
          flowId={flowId}
        />
      </Modal>
    </>
  );
}; 